import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { IconContext } from "react-icons";

import './App.scss';

// Eager: courier-critical first-paint path (scanner home + scan flow)
import Home from "../pages/Home/Home";
import Courier from "../pages/Courier/Courier";
import CourierOrder from '../pages/Courier/CourierOrder';
import CourierWarehouse from '../pages/Courier/CourierWarehouse';

import Incoming from "../pages/Incoming/Incoming";
import Dispatch from "../pages/Dispatch/Dispatch";

import Settings from "../pages/Settings/Settings";
import Tools from "../pages/Tools/Tools";

import ScannerLayout, { appLoader } from "../pages/ScannerLayout";
import DashboardLayout, { dashboardLoader } from "../pages/DashboardLayout";
import ProtectedRoute from './ProtectedRoute';

import AccessDenied from '../pages/AccessDenied';
import ErrorPage from '../pages/ErrorPage';

import { routeRoles } from '../config/roles';

// Lazy: dashboard + Configuration screens — couriers (majority install) never load these.
const WarehouseDashboard = lazy(() => import("../pages/WarehouseDashboard/WarehouseDashboard"));
const PartnerDashboard = lazy(() => import("../pages/PartnerDashboard/PartnerDashboard"));
const VehiclesScreen = lazy(() => import("../pages/Configuration/VehiclesScreen"));
const ServiceAreasScreen = lazy(() => import("../pages/Configuration/ServiceAreasScreen"));
const PricingScreen = lazy(() => import("../pages/Configuration/PricingScreen"));

// Minimal centred fallback shown briefly while a lazy chunk downloads.
const LazyFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2em' }}>
    Loading&hellip;
  </div>
);
const lazyRoute = (node: React.ReactNode) => <Suspense fallback={<LazyFallback />}>{node}</Suspense>;

const router = createBrowserRouter([
  // Branch 1: Mobile scanner layout
  {
    path: '/',
    element: <ScannerLayout />,
    loader: appLoader,
    errorElement: <ErrorPage />,
    children: [
      { path: '/', element: <Home />},
      {
        element: <ProtectedRoute requiredRoles={routeRoles['/courier']} />,
        children: [
          { path: '/courier', element: <Courier /> },
          { path: '/courier/order/:order/:type', element: <CourierOrder /> },
          { path: '/courier/warehouse/:warehouse', element: <CourierWarehouse /> },
        ]
      },
      {
        element: <ProtectedRoute requiredRoles={routeRoles['/incoming']} />,
        children: [
          { path: '/incoming', element: <Incoming /> },
        ]
      },
      {
        element: <ProtectedRoute requiredRoles={routeRoles['/dispatch']} />,
        children: [
          { path: '/dispatch', element: <Dispatch /> },
        ]
      },
      { path: '/settings', element: <Settings /> },
      { path: '/tools', element: <Tools /> },
      // Configuration screens — visibility is enforced inside each screen
      // via `user.role`. The booking-portal admin remains the canonical
      // place to write pricing; the scanner offers per-role read or write
      // matching the role matrix in LOGISTICS_DASHBOARDS_SELF_CONFIG_PROMPT.md.
      { path: '/configuration/vehicles', element: lazyRoute(<VehiclesScreen />) },
      { path: '/configuration/service-areas', element: lazyRoute(<ServiceAreasScreen />) },
      { path: '/configuration/pricing', element: lazyRoute(<PricingScreen />) },
      // Redirects from old dashboard URLs
      { path: '/partner', element: <Navigate to="/dashboard/partner" replace /> },
      { path: '/warehouse', element: <Navigate to="/dashboard/warehouse" replace /> },
      { path: '/dashboard-access-denied', element: <AccessDenied /> },
    ]
  },
  // Branch 2: Desktop dashboard layout
  {
    path: '/dashboard',
    element: <DashboardLayout />,
    loader: dashboardLoader,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <ProtectedRoute requiredRoles={routeRoles['/dashboard/warehouse']} requireDashboard />,
        children: [
          { path: 'warehouse', element: lazyRoute(<WarehouseDashboard />) },
        ]
      },
      {
        element: <ProtectedRoute requiredRoles={routeRoles['/dashboard/partner']} requireDashboard />,
        children: [
          { path: 'partner', element: lazyRoute(<PartnerDashboard />) },
        ]
      },
    ]
  },
]);


const App = () => {
  return (
    <IconContext.Provider value={{ className: 'icon' }}>
      <RouterProvider router={router}></RouterProvider>
    </IconContext.Provider>
  );
}

export default App;
